/* eslint-disable */

import React, { useState, useEffect, useRef, useCallback } from "react";
import "./PlaceholderPage.css";
import {
  MDBRow,
  MDBCol,
  MDBInput,
  MDBSpinner,
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBSwitch,
  MDBIcon,
} from "mdb-react-ui-kit";
import { useMessageBox } from "../components/Common/MessageBox";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const API_SYNC_BASE_URL = API_HOST + "/api/v1/getUniqueMetafields";
const API_DB_BASE_URL = API_HOST + "/api/v1/getShopifyMetafieldsFromDB";
const API_SHOPIFY_SETTINGS_BASE_URL = API_HOST + "/api/shopify-settings";

const ShopifySettingsPage = () => {
  const [metafields, setMetafields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const { showError, showSuccess, MessageBoxComponent } = useMessageBox();

  // SHOPIFY SETTINGS STATE
  const [shopifySettings, setShopifySettings] = useState({
    location_set: "",
    shipping_required: false,
    exists: false,
    id: null,
  });

  // LOCATIONS STATE
  const [shopifyLocations, setShopifyLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsFetched, setSettingsFetched] = useState(false);

  // POPUP STATE
  const [showPopup, setShowPopup] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const pollRef = useRef(null);
  const initialLoadDone = useRef(false);

  const companyId = localStorage.getItem("company_id");
  const token = localStorage.getItem("token");

  // =====================================
  // FETCH METAFIELDS FROM DB
  // =====================================
  const fetchMetafieldsFromDB = useCallback(
    async (silent = false) => {
      if (silent && metafields.length > 0) {
        return;
      }

      try {
        if (!silent) setLoading(true);
        setError("");

        if (!companyId) {
          setError("Company ID not found. Please login again.");
          return;
        }

        console.log("📡 Fetching metafields...", silent ? "(silent)" : "");
        const res = await fetch(`${API_DB_BASE_URL}/${companyId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const result = await res.json();

        if (!result.success) {
          throw new Error(result.message || "Failed to fetch metafields");
        }

        setMetafields(result.data.unique_metafields || []);
        setLastSyncedAt(result.data.last_synced_at || null);
      } catch (err) {
        console.error("Fetch Error:", err);
        setError(err.message || "Something went wrong");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [companyId, token, metafields.length],
  );

  // =====================================
  // FETCH SHOPIFY LOCATIONS
  // =====================================
  const fetchShopifyLocations = async () => {
    if (!companyId) return;

    try {
      setLocationsLoading(true);
      const res = await fetch(
        `${API_SHOPIFY_SETTINGS_BASE_URL}/locations/${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to fetch locations");
      }

      setShopifyLocations(result.locations || []);
    } catch (err) {
      console.error("Locations fetch error:", err);
      showError("Failed to load Shopify locations");
    } finally {
      setLocationsLoading(false);
    }
  };

  // =====================================
  // FETCH SHOPIFY SETTINGS
  // =====================================
  const fetchShopifySettings = useCallback(async () => {
    if (!companyId || settingsFetched) return;

    try {
      setSettingsLoading(true);
      const res = await fetch(`${API_SHOPIFY_SETTINGS_BASE_URL}/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to fetch settings");
      }

      if (result.exists) {
        setShopifySettings({
          location_set: result.settings.location_set || "",
          shipping_required:
            result.settings.shipping_required === 1 ||
            result.settings.shipping_required === true,
          exists: true,
          id: result.settings.id,
        });
      } else {
        setShopifySettings({
          location_set: "",
          shipping_required: false,
          exists: false,
          id: null,
        });
      }

      setSettingsFetched(true);
    } catch (err) {
      console.error("Settings fetch error:", err);
      showError(err.message || "Failed to load settings");
      setSettingsFetched(true);
    } finally {
      setSettingsLoading(false);
    }
  }, [companyId, token, showError, settingsFetched]);

  // =====================================
  // INITIAL LOAD
  // =====================================
  useEffect(() => {
    if (initialLoadDone.current) {
      return;
    }

    initialLoadDone.current = true;

    const loadData = async () => {
      await fetchMetafieldsFromDB();
      await fetchShopifySettings();
    };

    loadData();
  }, []);

  // =====================================
  // SYNC NOW
  // =====================================
  const handleSyncNow = async () => {
    try {
      setError("");

      if (!companyId) {
        setError("Company ID not found. Please login again.");
        return;
      }

      const res = await fetch(`${API_SYNC_BASE_URL}/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (!result.success) {
        throw new Error(result.message || "Sync failed");
      }

      setShowPopup(true);

      // Polling start karo
      startPolling();
    } catch (err) {
      console.error("Sync Error:", err);
      setError(err.message || "Sync failed");
    }
  };

  // =====================================
  // POLLING
  // =====================================
  const startPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
    }

    let pollCount = 0;
    const MAX_POLLS = 5;

    pollRef.current = setInterval(async () => {
      pollCount++;
      console.log(`⏳ Polling... (${pollCount}/${MAX_POLLS})`);

      await fetchMetafieldsFromDB(true);

      if (pollCount >= MAX_POLLS) {
        console.log("🛑 Polling stopped after max attempts");
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 3000);
  }, [fetchMetafieldsFromDB]);

  // =====================================
  // CLEANUP
  // =====================================
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // =====================================
  // OPEN SETTINGS MODAL
  // =====================================
  const handleOpenSettingsModal = async () => {
    await fetchShopifyLocations(); // Har baar fresh locations fetch karo
    setShowSettingsModal(true);
  };

  // =====================================
  // SAVE SHOPIFY SETTINGS
  // =====================================
  const handleSaveSettings = async () => {
    try {
      setSettingsLoading(true);

      const method = shopifySettings.exists ? "PUT" : "POST";

      const res = await fetch(`${API_SHOPIFY_SETTINGS_BASE_URL}/${companyId}`, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          location_set: shopifySettings.location_set,
          shipping_required: shopifySettings.shipping_required,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to save settings");
      }

      setShopifySettings({
        location_set: result.settings.location_set,
        shipping_required:
          result.settings.shipping_required === 1 ||
          result.settings.shipping_required === true,
        exists: true,
        id: result.settings.id,
      });

      showSuccess("Settings saved successfully!");
      setShowSettingsModal(false);
    } catch (err) {
      console.error("Save settings error:", err);
      showError(err.message || "Failed to save settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  // Helper function to check if current location is active
  const isCurrentLocationActive = () => {
    if (!shopifySettings.location_set) return false;
    const location = shopifyLocations.find(
      (l) => l.name === shopifySettings.location_set,
    );
    return location?.isActive || false;
  };

  return (
    <div className="placeholder-page">
      <h1 className="page-title">Shopify Settings</h1>
      <p className="page-description">
        View all unique Shopify metafields used across your products.
      </p>

      {/* Location Settings Card - Upar Dikhega */}
      <div className="settings-card mb-4">
        <MDBRow className="align-items-center">
          <MDBCol>
            <h5 className="mb-0">
              <MDBIcon fas icon="store" className="me-2" />
              Store Location Settings
            </h5>
          </MDBCol>
          <MDBCol className="text-end">
            <MDBBtn color="primary" size="sm" onClick={handleOpenSettingsModal}>
              <MDBIcon fas icon="cog" className="me-2" />
              {shopifySettings.exists ? "Edit Location" : "Configure Location"}
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        {shopifySettings.exists ? (
          <MDBRow className="mt-3">
            <MDBCol md="6">
              <div
                className={`p-3 rounded ${isCurrentLocationActive() ? "bg-light-success" : "bg-light-danger"}`}
              >
                <small className="text-muted d-block">Selected Location</small>
                <div className="d-flex align-items-center">
                  <strong className="fs-5 me-2">
                    {shopifySettings.location_set}
                  </strong>
                  {isCurrentLocationActive() ? (
                    <span className="badge bg-success">ACTIVE</span>
                  ) : (
                    <span className="badge bg-danger">INACTIVE</span>
                  )}
                </div>
              </div>
            </MDBCol>
            <MDBCol md="6">
              <div className="p-3 bg-light rounded">
                <small className="text-muted d-block">Shipping Required</small>
                <strong className="fs-5">
                  {shopifySettings.shipping_required ? (
                    <span className="text-success">✓ Yes</span>
                  ) : (
                    <span className="text-secondary">✗ No</span>
                  )}
                </strong>
              </div>
            </MDBCol>
          </MDBRow>
        ) : (
          <div className="text-center p-4 text-muted">
            <MDBIcon fas icon="info-circle" size="2x" className="mb-3" />
            <p>
              No location configured yet. Click the button above to select a
              location from your Shopify store.
            </p>
          </div>
        )}
      </div>

      {/* Metafields Card */}
      <div className="settings-card">
        <MDBRow className="align-items-center mb-2">
          <MDBCol>
            {lastSyncedAt && (
              <small className="text-muted">
                Last synced: {new Date(lastSyncedAt).toLocaleString()}
              </small>
            )}
          </MDBCol>

          <MDBCol className="text-end">
            <MDBBtn color="primary" onClick={handleSyncNow}>
              <MDBIcon fas icon="sync" className="me-2" />
              Sync Now
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        {error && <p className="text-danger text-center">{error}</p>}

        {loading && (
          <div className="text-center my-4">
            <MDBSpinner />
            <p className="mt-2">Loading metafields...</p>
          </div>
        )}

        {!loading && metafields.length === 0 && !error && (
          <p className="text-muted text-center">
            No metafields found for this store.
          </p>
        )}

        {metafields.length > 0 && (
          <>
            {/* Column Headings */}
            <MDBRow className="mb-2 fw-bold text-center">
              <MDBCol md="2">Name</MDBCol>
              <MDBCol md="2">Key</MDBCol>
              <MDBCol md="2">Type</MDBCol>
              <MDBCol md="2">Namespace</MDBCol>
              <MDBCol md="2">isActive</MDBCol>
              <MDBCol md="2">Actions</MDBCol>
            </MDBRow>

            {/* Metafields Rows */}
            {metafields.map((mf, index) => (
              <MDBRow
                key={index}
                className="mb-3 align-items-center text-center"
              >
                <MDBCol md="2">
                  <MDBInput label="Name" value={mf.name} disabled />
                </MDBCol>
                <MDBCol md="2">
                  <MDBInput label="Key" value={mf.key} disabled />
                </MDBCol>
                <MDBCol md="2">
                  <MDBInput label="Type" value={mf.type} disabled />
                </MDBCol>
                <MDBCol md="2">
                  <MDBInput label="namespace" value={mf.namespace} disabled />
                </MDBCol>
                <MDBCol md="2" className="d-flex justify-content-center">
                  <MDBSwitch
                    id={`activeSwitch-${index}`}
                    checked={mf.isactive === 1}
                    label=""
                    onChange={async (e) => {
                      const newStatus = e.target.checked ? 1 : 0;
                      try {
                        const res = await fetch(
                          `${API_HOST}/api/v1/updateMetafieldIsActive`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                              metafield_id: mf.id,
                              isactive: newStatus,
                            }),
                          },
                        );
                        const result = await res.json();
                        if (!res.ok || !result.success)
                          throw new Error(result.message);
                        setMetafields((prev) =>
                          prev.map((item) =>
                            item.id === mf.id
                              ? { ...item, isactive: newStatus }
                              : item,
                          ),
                        );
                      } catch (err) {
                        console.error("Failed to update isActive:", err);
                        showError("Failed to update isActive. Try again.");
                      }
                    }}
                  />
                </MDBCol>
                <MDBCol md="2">
                  <span className="text-muted">—</span>
                </MDBCol>
              </MDBRow>
            ))}
          </>
        )}
      </div>

      {/* SETTINGS MODAL - WITH ACTIVE/INACTIVE LOCATION DROPDOWN */}
      <MDBModal
        open={showSettingsModal}
        setOpen={setShowSettingsModal}
        staticBackdrop
      >
        <MDBModalDialog centered size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="cog" className="me-2" />
                {shopifySettings.exists
                  ? "Edit Store Location"
                  : "Select Store Location"}
              </MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody>
              <p className="text-muted mb-3">
                Select a location from your Shopify store. This location will be
                used for all products.
              </p>

              {/* Location Dropdown - WITH ACTIVE/INACTIVE STATUS */}
              <div className="mb-4">
                <label className="form-label fw-bold">
                  <MDBIcon fas icon="map-marker-alt" className="me-2" />
                  Shopify Locations
                </label>

                {locationsLoading ? (
                  <div className="text-center p-3 border rounded">
                    <MDBSpinner size="sm" />
                    <span className="ms-2">Loading locations...</span>
                  </div>
                ) : (
                  <>
                    <select
                      className="form-select form-select-lg"
                      value={shopifySettings.location_set}
                      onChange={(e) =>
                        setShopifySettings((prev) => ({
                          ...prev,
                          location_set: e.target.value,
                        }))
                      }
                      style={{
                        border: shopifySettings.location_set
                          ? isCurrentLocationActive()
                            ? "2px solid #28a745"
                            : "2px solid #dc3545"
                          : "1px solid #ced4da",
                      }}
                    >
                      <option value="">-- Select a location --</option>

                      {/* Pehle ACTIVE locations dikhao (selectable) */}
                      {shopifyLocations.filter((loc) => loc.isActive).length >
                        0 && (
                        <optgroup label="✅ ACTIVE LOCATIONS (available)">
                          {shopifyLocations
                            .filter((loc) => loc.isActive)
                            .map((loc) => (
                              <option
                                key={loc.id}
                                value={loc.name}
                                style={{ color: "#28a745", fontWeight: "bold" }}
                              >
                                {loc.name} {loc.address && `- ${loc.address}`}
                              </option>
                            ))}
                        </optgroup>
                      )}

                      {/* Phir INACTIVE locations dikhao (disabled) */}
                      {shopifyLocations.filter((loc) => !loc.isActive).length >
                        0 && (
                        <optgroup label="❌ INACTIVE LOCATIONS (cannot use)">
                          {shopifyLocations
                            .filter((loc) => !loc.isActive)
                            .map((loc) => (
                              <option
                                key={loc.id}
                                value={loc.name}
                                disabled
                                style={{
                                  color: "#999",
                                  backgroundColor: "#f5f5f5",
                                  fontStyle: "italic",
                                }}
                              >
                                {loc.name} {loc.address && `- ${loc.address}`}{" "}
                                (DEACTIVATED)
                              </option>
                            ))}
                        </optgroup>
                      )}
                    </select>

                    {/* Warning messages */}
                    {shopifySettings.location_set &&
                      !isCurrentLocationActive() && (
                        <div className="alert alert-danger mt-2 p-2">
                          <MDBIcon
                            fas
                            icon="exclamation-triangle"
                            className="me-2"
                          />
                          <strong>Warning:</strong> "
                          {shopifySettings.location_set}" is DEACTIVATED!
                          Products cannot be assigned to this location. Please
                          select an active location.
                        </div>
                      )}

                    {!shopifySettings.location_set && (
                      <small className="text-warning d-block mt-2">
                        <MDBIcon fas icon="info-circle" className="me-1" />
                        Please select an ACTIVE location for product uploads
                      </small>
                    )}
                  </>
                )}

                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">
                    <span className="badge bg-success me-1">Active</span>{" "}
                    locations can be used
                  </small>
                  <small className="text-muted">
                    <span className="badge bg-secondary me-1">Inactive</span>{" "}
                    locations are disabled
                  </small>
                </div>
              </div>

              {/* Shipping Required Switch */}
              <div className="d-flex align-items-center mt-4">
                <MDBSwitch
                  id="shippingRequiredSwitch"
                  checked={shopifySettings.shipping_required}
                  onChange={(e) =>
                    setShopifySettings((prev) => ({
                      ...prev,
                      shipping_required: e.target.checked,
                    }))
                  }
                  label="Shipping Required"
                />
              </div>

              {/* Refresh Locations Button */}
              <div className="text-end mt-3">
                <MDBBtn
                  color="link"
                  size="sm"
                  onClick={fetchShopifyLocations}
                  disabled={locationsLoading}
                >
                  <MDBIcon fas icon="sync" className="me-1" />
                  Refresh Locations
                </MDBBtn>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn
                color="secondary"
                onClick={() => setShowSettingsModal(false)}
              >
                Cancel
              </MDBBtn>
              <MDBBtn
                color="primary"
                onClick={handleSaveSettings}
                disabled={
                  settingsLoading ||
                  !shopifySettings.location_set.trim() ||
                  // Agar selected location inactive hai to save button disable
                  (shopifySettings.location_set && !isCurrentLocationActive())
                }
              >
                {settingsLoading ? <MDBSpinner size="sm" /> : "Save Settings"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* Sync Popup */}
      <MDBModal staticBackdrop open={showPopup} setOpen={setShowPopup}>
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Sync Started</MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody className="text-center">
              Metafields sync started. You can continue working.
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn onClick={() => setShowPopup(false)}>OK</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MessageBoxComponent />
    </div>
  );
};

export default ShopifySettingsPage;
